import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import type { InjectedFormProps } from 'redux-form';
import { Field, FieldArray } from 'redux-form';

import type { EquipmentCostViewModel } from 'ab-viewModels/equipmentCost.viewModel';
import type LocationOptionVM from 'ab-viewModels/location/option.viewModel';
import type * as ResourceStatusesViewModel from 'ab-viewModels/resources/resourceStatuses.viewModel';
import type { SkillViewModel } from 'ab-viewModels/skill.viewModel';

import type { EquipmentRequestModel } from 'ab-requestModels/equipment.requestModel';

import * as ArrayUtils from '@acceligentllc/shared/utils/array';
import { formatDate } from '@acceligentllc/shared/utils/time';

import { BlobStorageImageSizeContainer } from '@acceligentllc/shared/enums/blobStorageImageSizeContainer';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import { bemElement } from 'af-utils/bem.util';

import { MAX_EQUIPMENT_SPECIFICATION_LENGTH, DEFAULT_EQUIPMENT_IMAGE } from 'ab-common/constants/value';

import { Button, SegmentLabel } from '@acceligentllc/storybook';

import Checkbox from 'af-fields/Checkbox';
import DateInput from 'af-fields/DateInput';
import Dropdown from 'af-fields/Dropdown';
import Input from 'af-fields/Input';
import type { OwnProps as MultiTagSelectProps } from 'af-fields/MultiTagSelect';
import MultiTagSelect from 'af-fields/MultiTagSelect';
import Select from 'af-fields/SelectField';
import Textarea from 'af-fields/Textarea';

import BackButton from 'af-components/BackButton';
import ImageTag from 'af-components/Image';
import ImageUpload from 'af-components/ImageUpload';
import LabelWithPills from 'af-components/LockedPillsValue';
import SubmitButton from 'af-components/SubmitButton';

import * as EquipmentActions from 'af-actions/equipment';
import * as EquipmentCostActions from 'af-actions/equipmentCost';
import * as LocationActions from 'af-actions/location';
import * as SkillActions from 'af-actions/skill';

import * as Helpers from './helpers';
import styles from './styles.module.scss';
import SkillMultiValueLabel from '../Shared/SkillMultiValueLabel';
import SkillOption from '../Shared/SkillOption';

const DEFAULT_AVAILABLE = Object.freeze({
	id: -1,
	name: 'Available',
	index: 0,
	available: true,
	isDeleted: false,
});

const EquipmentCostSelect = Select as unknown as new () => typeof Select<EquipmentCostViewModel>;
const LocationSelect = Select as unknown as new () => typeof Select<LocationOptionVM>;

interface PathParams {
	id?: string;
}

type FormProps = InjectedFormProps<EquipmentRequestModel>;

interface OwnProps {
	onSubmit: (form: EquipmentRequestModel) => Promise<void>;
	formActionWrapper: {
		selector: <T extends keyof EquipmentRequestModel>(fieldName: T) => EquipmentRequestModel[T];
	};
}

const YEAR_OPTION_START = 1970;
const NUMBER_OF_FUTURE_YEAR_OPTIONS = 10;
const YEAR_OPTIONS = ArrayUtils
	.range(YEAR_OPTION_START, (new Date()).getFullYear() + 1 + NUMBER_OF_FUTURE_YEAR_OPTIONS)
	.map((_year: number) => ({ id: _year, label: _year.toString() }));

type Props = OwnProps & ConnectedProps<typeof connector> & CustomRouteComponentProps<PathParams> & FormProps;

interface State {
	companyEquipmentStatuses: ResourceStatusesViewModel.List;
	equipmentCost: Nullable<EquipmentCostViewModel>;
	frontImage: string | File | null;
	frontImageUrl: string | null;
	backImage: string | File | null;
	backImageUrl: string | null;
	hasReceivedInitialItem: boolean;
	deleteFrontImage: boolean;
	deleteBackImage: boolean;
	skillList: SkillViewModel[];
	equipmentCostOptions: EquipmentCostViewModel[];
	locationOptions: LocationOptionVM[];
	showFrontImageUpload: boolean;
	showBackImageUpload: boolean;
}

class EquipmentForm extends React.PureComponent<Props, State> {

	state: State = {
		companyEquipmentStatuses: [],
		equipmentCost: null,
		hasReceivedInitialItem: false,
		frontImage: null,
		frontImageUrl: null,
		backImage: null,
		backImageUrl: null,
		deleteFrontImage: false,
		deleteBackImage: false,
		skillList: [],
		equipmentCostOptions: [],
		locationOptions: [],
		showFrontImageUpload: false,
		showBackImageUpload: false,
	};

	static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
		const { initialized, formActionWrapper } = props;
		const { hasReceivedInitialItem } = state;

		if (!hasReceivedInitialItem && initialized) {
			const equipmentCost = formActionWrapper.selector('equipmentCost');
			const frontImage = formActionWrapper.selector('frontImageUrl');
			const frontImageUrl = formActionWrapper.selector('frontImageUrl');
			const backImage = formActionWrapper.selector('backImageUrl');
			const backImageUrl = formActionWrapper.selector('backImageUrl');
			return {
				hasReceivedInitialItem: true,
				equipmentCost,
				frontImage,
				frontImageUrl,
				backImage,
				backImageUrl,
			};
		}

		return null;
	}

	async componentDidMount() {
		const { findAllStatusesForCompany, findAllSkillsByQuery, findAllEquipmentCostsByQuery, findAllLocations } = this.props;
		const [
			{ available, unavailable },
			skills,
			equipmentCostOptions,
			locationOptions,
		] = await Promise.all([
			findAllStatusesForCompany(),
			findAllSkillsByQuery(''),
			findAllEquipmentCostsByQuery(''),
			findAllLocations(),
		]);
		this.setState(() => ({
			companyEquipmentStatuses: [DEFAULT_AVAILABLE, ...available, ...unavailable],
			skillList: skills,
			equipmentCostOptions,
			locationOptions,
		}));
	}

	onSubmit = async (form: EquipmentRequestModel) => {
		const { onSubmit } = this.props;
		const { frontImage, backImage, deleteBackImage, deleteFrontImage } = this.state;

		const newForm = { ...form };

		newForm.frontImageUrl = frontImage as string;
		newForm.backImageUrl = backImage as string;
		newForm.deleteFrontImage = deleteFrontImage;
		newForm.deleteBackImage = deleteBackImage;
		newForm.inspectionStickerExpiration = form.inspectionStickerExpiration
			? formatDate(form.inspectionStickerExpiration, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY)
			: null;
		newForm.registrationExpiration = form.registrationExpiration
			? formatDate(form.registrationExpiration, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY)
			: null;
		await onSubmit(newForm);
	};

	// Image

	setFrontImage = (
		file: Nullable<File>,
		url: Nullable<string>,
		isDelete: boolean
	) => this.setState(() => ({ frontImage: file, frontImageUrl: url, deleteFrontImage: isDelete }));

	setBackImage = (
		file: Nullable<File>,
		url: Nullable<string>,
		isDelete: boolean
	) => this.setState(() => ({ backImage: file, backImageUrl: url, deleteBackImage: isDelete }));

	deleteFrontImage = () => this.setFrontImage(null, null, true);

	deleteBackImage = () => this.setBackImage(null, null, true);

	openFrontImageUpload = () => this.setState(() => ({ showFrontImageUpload: true }));
	closeFrontImageUpload = () => this.setState(() => ({ showFrontImageUpload: false }));

	openBackImageUpload = () => this.setState(() => ({ showBackImageUpload: true }));
	closeBackImageUpload = () => this.setState(() => ({ showBackImageUpload: false }));

	onFrontImageUploadSave = (frontImage: File, frontImageUrl: string) => this.setFrontImage(frontImage, frontImageUrl, false);

	onBackImageUploadSave = (backImage: File, backImageUrl: string) => this.setBackImage(backImage, backImageUrl, false);

	// Equipment Cost

	onEquipmentCostChange = (equipmentCost: EquipmentCostViewModel) => {
		const { change } = this.props;
		if (equipmentCost) {
			this.setState(() => ({ equipmentCost }));
		} else {
			change('location', null);
		}
	};

	// Location

	onLocationChange = (location: LocationOptionVM) => {
		const { change } = this.props;
		if (!location) {
			change('location', null);
		}
	};

	// Render

	render() {
		const {
			handleSubmit,
			submitting,
			invalid,
		} = this.props;
		const {
			companyEquipmentStatuses,
			frontImage,
			backImage,
			frontImageUrl,
			backImageUrl,
			equipmentCost,
			skillList,
			locationOptions,
			equipmentCostOptions,
			showFrontImageUpload,
			showBackImageUpload,
		} = this.state;

		const imageUploadButtonClassName = bemElement('image-upload', 'button');

		return (
			<div>
				<div className="form-box">
					<Row className="row--flex">
						<div className="avatar__container avatar__container--double">
							<ImageUpload
								closeModal={this.closeFrontImageUpload}
								onSave={this.onFrontImageUploadSave}
								showModal={showFrontImageUpload}
							/>
							<ImageUpload
								closeModal={this.closeBackImageUpload}
								onSave={this.onBackImageUploadSave}
								showModal={showBackImageUpload}
							/>
							<div className="avatar">
								<span className="avatar__label">Front</span>
								<ImageTag
									fallbackSrc={DEFAULT_EQUIPMENT_IMAGE}
									minSize={BlobStorageImageSizeContainer.SIZE_200X200}
									src={frontImageUrl}
									tryOriginal={true}
									tryRoot={true}
								/>
								<div className={bemElement('image-upload', 'buttons')}>
									<Button
										customStyleClass={imageUploadButtonClassName}
										icon="ActionImport"
										onClick={this.openFrontImageUpload}
										style="secondary"
										tooltip="Upload"
										type="button"
									/>
									<Button
										customStyleClass={imageUploadButtonClassName}
										disabled={!frontImage && !frontImageUrl}
										icon="Delete"
										onClick={this.deleteFrontImage}
										style="danger"
										tooltip="Delete"
										type="button"
									/>
								</div>
							</div>
							<div className="avatar">
								<span className="avatar__label">Back</span>
								<ImageTag
									fallbackSrc={DEFAULT_EQUIPMENT_IMAGE}
									minSize={BlobStorageImageSizeContainer.SIZE_200X200}
									src={backImageUrl}
									tryOriginal={true}
									tryRoot={true}
								/>
								<div className={bemElement('image-upload', 'buttons')}>
									<Button
										customStyleClass={imageUploadButtonClassName}
										icon="ActionImport"
										onClick={this.openBackImageUpload}
										style="secondary"
										tooltip="Upload"
									/>
									<Button
										customStyleClass={imageUploadButtonClassName}
										disabled={!backImage && !backImageUrl}
										icon="Delete"
										onClick={this.deleteBackImage}
										style="danger"
										tooltip="Delete"
									/>
								</div>
							</div>
						</div>
						<div className="avatar__neighbour">
							<Row className="row--padded">
								<Col md={8}>
									<Field
										component={Input}
										label={Helpers.renderEquipmentIdLabel()}
										maxCharacters={17}
										name="code"
										placeholder="Enter Equipment ID"
										type="text"
									/>
								</Col>
								<Col md={8}>
									<Field
										component={Input}
										label="Equipment Specification"
										maxCharacters={MAX_EQUIPMENT_SPECIFICATION_LENGTH}
										name="specification"
										placeholder="Enter Equipment Specification"
										type="text"
									/>
								</Col>
								<Col md={8}>
									<Field
										component={Dropdown}
										id="equipmentStatusId"
										label="Current Status *"
										name="equipmentStatusId"
										options={companyEquipmentStatuses}
										renderMenuItem={Helpers.renderResourceStatusMenuItem}
										valueKey="id"
										withCaret={true}
									/>
								</Col>
							</Row>
							<Row className="row--padded-bottom">
								<Col md={8}>
									<Field
										component={EquipmentCostSelect}
										filterOption={Helpers.filterEquipmentCosts}
										formatOptionLabel={Helpers.formatEquipmentCostOptionLabel}
										getOptionLabel={Helpers.getEquipmentCostOptionLabel}
										getOptionValue={Helpers.getEquipmentCostOptionValue}
										label="Equipment Cost *"
										name="equipmentCost"
										onValueChange={this.onEquipmentCostChange}
										options={equipmentCostOptions}
										placeholder="Enter Equipment Cost"
									/>
								</Col>
								<Col md={8}>
									<Field
										component={LocationSelect}
										filterOption={Helpers.filterLocations}
										formatOptionLabel={Helpers.formatLocationOptionLabel}
										getOptionLabel={Helpers.getLocationOptionLabel}
										getOptionValue={Helpers.getLocationOptionValue}
										label="Home Office"
										name="location"
										onValueChange={this.onLocationChange}
										options={locationOptions}
										placeholder="Enter Home Office"
									/>
								</Col>
								<Col md={8}>
									<Field
										component={Checkbox}
										inline={true}
										label="Show on schedule board"
										name="showOnScheduleBoard"
									/>
								</Col>
							</Row>
						</div>
					</Row>
					<Row>
						<Col className="m-b-m m-t-m" sm={12}>
							<SegmentLabel label="Contacts" />
						</Col>
					</Row>
					<Row>
						<Col md={6}>
							<Field
								component={Input}
								label="First Contact"
								name="primaryContact"
								placeholder="Enter First Contact"
								type="text"
							/>
						</Col>
						<Col md={6}>
							<Field
								component={Input}
								label="Second Contact"
								name="secondaryContact"
								placeholder="Enter Second Contact"
								type="text"
							/>
						</Col>
						<Col md={12} />
					</Row>
					<Row>
						<Col className="m-b-m m-t-m" sm={12}>
							<SegmentLabel label="Skills and Licenses" />
						</Col>
					</Row>
					<Row>
						<Col md={24}>
							<FieldArray<MultiTagSelectProps<SkillViewModel>>
								component={MultiTagSelect}
								getOptionLabel={Helpers.getSkillOptionLabel}
								getOptionValue={Helpers.getSkillOptionValue}
								label="Skills"
								MultiValueLabel={SkillMultiValueLabel}
								name="skills"
								Option={SkillOption}
								options={skillList}
								placeholder="Choose skills"
							/>

						</Col>
					</Row>
					<Row className="row--padded">
						<Col md={24}>
							<LabelWithPills
								colorKey="color"
								items={equipmentCost?.skills ?? []}
								label="Skills Defined by Equipment Cost"
								labelKey="name"
							/>
						</Col>
					</Row>
					<Row className="row--padded">
						<Col md={12}>
							<Field
								component={Textarea}
								controlCursor={true}
								label="Licenses"
								name="licenses"
								placeholder="Enter Needed Licenses"
							/>
						</Col>
						<Col sm={12} />
					</Row>
					<Row className="row--padded">
						<Col md={6}>
							<Field
								component={Input}
								label="Plate Number"
								maxCharacters={9}
								name="plateNumber"
								placeholder="Enter Plate Number"
								type="text"
							/>
						</Col>
						<Col md={6}>
							<Field
								component={Input}
								label="Plate Type"
								maxCharacters={9}
								name="plateType"
								placeholder="Enter Plate Type"
								type="text"
							/>
						</Col>
						<Col md={6}>
							<Field
								component={Input}
								label="Registration State"
								maxCharacters={2}
								name="registrationState"
								placeholder="Enter Registration State"
								type="text"
							/>
						</Col>
						<Col md={6}>
							<Field
								component={DateInput}
								dateFormat={TimeFormat.DATE_ONLY}
								label="Registration Expiration"
								name="registrationExpiration"
								originalDateFormat={TimeFormat.DATE_ONLY}
								placeholderText={TimeFormat.DATE_ONLY}
							/>
						</Col>
					</Row>
					<Row className="row--padded">
						<Col md={6}>
							<Field
								component={DateInput}
								dateFormat={TimeFormat.DATE_ONLY}
								label="Inspection Expiration"
								name="inspectionStickerExpiration"
								originalDateFormat={TimeFormat.DATE_ONLY}
								placeholderText={TimeFormat.DATE_ONLY}
							/>
						</Col>
						<Col md={6}>
							<Field
								component={Input}
								label="VIN Number"
								maxCharacters={17}
								name="vinNumber"
								placeholder="Enter VIN"
								type="text"
							/>
						</Col>
					</Row>
					<Row className="row--padded">
						<Col md={6}>
							<Field
								component={Input}
								label="GVW"
								name="gvw"
								placeholder="Gross Vehicle Weight"
								type="number"
							/>
						</Col>
						<Col md={6}>
							<div className={styles.label}>Year</div>
							<Field
								component={Dropdown}
								id="year"
								labelKey={'label'}
								name="year"
								options={YEAR_OPTIONS}
								placeholder="Select Year"
								valueKey={'id'}
								withCaret={true}
							/>
						</Col>
						<Col md={6}>
							<Field
								component={Input}
								label="Make"
								maxCharacters={25}
								name="make"
								placeholder="Enter Make"
								type="text"
							/>
						</Col>
						<Col md={6}>
							<Field
								component={Input}
								label="Model"
								maxCharacters={25}
								name="model"
								placeholder="Enter Model"
								type="text"
							/>
						</Col>
					</Row>
					<Row className="row--padded">
						<Col md={6}>
							<Field
								component={Input}
								label="Drivers Alert ID"
								maxCharacters={25}
								name="driversAlertId"
								placeholder="Enter Drivers Alert ID"
								type="text"
							/>
						</Col>
						<Col md={6}>
							<Field
								component={Input}
								label="Toll Pass ID"
								maxCharacters={25}
								name="tollPassId"
								placeholder="Enter Toll Pass ID"
								type="text"
							/>
						</Col>
						<Col md={6}>
							<Field
								component={Input}
								label="HUT Number"
								maxCharacters={25}
								name="hutNumber"
								placeholder="Enter HUT Number"
								type="text"
							/>
						</Col>
					</Row>

					<Row className="row--submit">
						<BackButton />
						<SubmitButton
							disabled={invalid}
							onClick={handleSubmit(this.onSubmit)}
							reduxFormSubmitting={submitting}
							variant="primary"
						/>
					</Row>
				</div>
			</div>
		);
	}
}

function mapDispatchToProps() {
	return {
		findAllStatusesForCompany: EquipmentActions.findAllStatusesForCompany,
		findAllSkillsByQuery: SkillActions.findAllForCompanyByQuery,
		findAllEquipmentCostsByQuery: EquipmentCostActions.findAllByQuery,
		findAllLocations: LocationActions.findList,
	};
}

const connector = connect(null, mapDispatchToProps());

export default connector<React.ComponentClass<OwnProps>>(EquipmentForm);
